import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Send, MessageCircle } from "lucide-react";

export default function Messages() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const withUser = params.get("with");
  const listingId = params.get("listing");

  const [convs, setConvs] = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  const [activeOther, setActiveOther] = useState(null);
  const [activeListing, setActiveListing] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (user === false) { navigate("/login?next=/messages"); }
  }, [user, navigate]);

  const loadConvs = async () => {
    try {
      const { data } = await api.get("/conversations");
      setConvs(data);
      return data;
    } catch { return []; }
  };

  useEffect(() => {
    (async () => {
      if (!user || !user.id) return;
      setLoading(true);
      const list = await loadConvs();
      if (withUser) {
        // Open or create thread
        setActiveOther({ id: withUser });
        setActiveListing(listingId ? { id: listingId } : null);
        await loadThread(withUser, listingId);
      } else if (list.length) {
        openConv(list[0]);
      }
      setLoading(false);
    })();
    // eslint-disable-next-line
  }, [user, withUser, listingId]);

  const loadThread = async (otherId, lid) => {
    const params = { with_user: otherId };
    if (lid) params.listing_id = lid;
    const { data } = await api.get("/messages", { params });
    setMessages(data);
    // Enrich other user and listing details from conversations list if available
    const conv = convs.find((c) => c.other_user?.id === otherId && (!lid || c.listing?.id === lid));
    if (conv) {
      setActiveKey(conv.conversation_key);
      if (conv.other_user) setActiveOther(conv.other_user);
      if (conv.listing) setActiveListing(conv.listing);
    }
    // If missing, fetch listing
    if (lid && (!conv || !conv.listing)) {
      try {
        const { data: l } = await api.get(`/listings/${lid}`);
        setActiveListing({ id: l.id, title: l.title, price: l.price, image: l.images?.[0] });
        if (!activeOther?.name && l.seller) setActiveOther(l.seller);
      } catch {}
    }
    setTimeout(() => scrollRef.current?.scrollTo({ top: 1e9, behavior: "smooth" }), 50);
  };

  const openConv = async (conv) => {
    setActiveKey(conv.conversation_key);
    setActiveOther(conv.other_user);
    setActiveListing(conv.listing);
    const params = { with_user: conv.other_user?.id };
    if (conv.listing?.id) params.listing_id = conv.listing.id;
    const { data } = await api.get("/messages", { params });
    setMessages(data);
    setTimeout(() => scrollRef.current?.scrollTo({ top: 1e9, behavior: "smooth" }), 50);
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !activeOther?.id) return;
    const body = {
      recipient_id: activeOther.id,
      listing_id: activeListing?.id || null,
      text: text.trim(),
    };
    setText("");
    try {
      const { data: msg } = await api.post("/messages", body);
      setMessages((prev) => [...prev, msg]);
      loadConvs();
      setTimeout(() => scrollRef.current?.scrollTo({ top: 1e9, behavior: "smooth" }), 30);
    } catch {}
  };

  if (!user || !user.id) return null;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 fade-in">
      <h1 className="font-display text-3xl mb-6">{t("messages.title")}</h1>

      <div className="grid md:grid-cols-[320px_1fr] rounded-2xl border border-border overflow-hidden min-h-[65vh] bg-card">
        {/* Sidebar */}
        <aside className="border-r border-border bg-muted/30 max-h-[75vh] overflow-y-auto">
          {loading ? (
            <div className="p-4 text-sm text-muted-foreground">{t("messages.loading")}</div>
          ) : convs.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground text-center">
              <MessageCircle className="w-8 h-8 mx-auto mb-3 opacity-40" />
              {t("messages.empty")}
            </div>
          ) : (
            <ul>
              {convs.map((c) => (
                <li key={c.conversation_key}>
                  <button
                    data-testid={`conv-item-${c.conversation_key}`}
                    onClick={() => openConv(c)}
                    className={`w-full text-left px-4 py-3 border-b border-border/60 flex gap-3 hover:bg-background transition-colors ${
                      activeKey === c.conversation_key ? "bg-background" : ""
                    }`}
                  >
                    <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center font-medium shrink-0">
                      {(c.other_user?.name || "?").slice(0, 1)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm truncate">{c.other_user?.name || "Utilisateur"}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {c.listing?.title ? `${c.listing.title} · ` : ""}{c.last_message}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* Thread */}
        <section className="flex flex-col max-h-[75vh]">
          {activeOther ? (
            <>
              <div className="px-5 py-3 border-b border-border flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center font-medium">
                  {(activeOther.name || "?").slice(0, 1)}
                </div>
                <div className="flex-1">
                  <div className="font-medium">{activeOther.name || "Utilisateur"}</div>
                  {activeListing && (
                    <Link
                      to={`/item/${activeListing.id}`}
                      className="text-xs text-primary hover:underline"
                      data-testid="thread-listing-link"
                    >
                      {t("messages.thread_about", { title: activeListing.title || "…" })}
                    </Link>
                  )}
                </div>
              </div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3 bg-background/50">
                {messages.length === 0 && (
                  <div className="text-center text-sm text-muted-foreground py-16">
                    {t("messages.empty_thread")}
                  </div>
                )}
                {messages.map((m) => {
                  const mine = m.sender_id === user.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div
                        data-testid={`msg-${m.id}`}
                        className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-[15px] leading-snug ${
                          mine
                            ? "bg-primary text-primary-foreground rounded-br-md"
                            : "bg-muted text-foreground rounded-bl-md"
                        }`}
                      >
                        {m.text}
                      </div>
                    </div>
                  );
                })}
              </div>
              <form onSubmit={send} className="p-3 border-t border-border flex gap-2 bg-background" data-testid="message-form">
                <input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={t("messages.input_placeholder")}
                  data-testid="message-input"
                  className="flex-1 h-11 rounded-full bg-muted px-4 focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm"
                />
                <Button
                  type="submit"
                  className="rounded-full h-11 w-11 p-0"
                  disabled={!text.trim()}
                  data-testid="message-send"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm p-8">
              {t("messages.select_conv")}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
